import type {
  AI_SPEND_INCLUSION_TYPES,
  CONTRACT_STATUS_TYPES,
  EXPORT_INCLUSION_TYPES,
  ExportFieldConfiguration,
} from "@/components/Export/types";
import type { IntentSignalScoreUi } from "@/components/Export/components/IntentThresholdSelector";
import type { FinancingEventsFilters } from "@/components/Export/financingEvents";
import type { MergersAcquisitionsFilters } from "@/components/Export/mergersAcquisitions";
import type { SelectableCustomColumn } from "@/components/Export/SpendSheet/types";
import type { CachedTopic } from "@/types/intent";
import type { ContactsFacExportSettings } from "./universePayloadBuilder";

export enum ExportCategory {
  Installs = "Installs",
  Contracts = "Contracts",
  IntentActivity = "Intent",
  FunctionalAreaIntelligence = "Functional Area Intelligence",
  CloudMaturity = "Cloud Maturity",
  ProductsAndVendors = "Products & Vendors",
  ITSpend = "IT Spend",
  // universe-json-export sections
  BuyerIntent = "Buyer Intent",
  Mentions = "Mentions",
  GenerativeAI = "Gen AI Navigator",
  RelativeSpend = "Relative IT Spend",
  RowBasedSpend = "Row-Based Spend",
  AISpend = "AI Spend",
  FinancingEvents = "Financing Events",
  MergersAcquisitions = "M&A Events",
  Contacts = "Contacts",
  ContactsFac = "Contacts + Tech Installs",
  Scores = "Scores",
}

export type ExportSettingItem = {
  main: string;
  subItems?: string[];
  /** Optional display-label overrides keyed by ExportCategory value. */
  subItemLabels?: Record<string, string>;
};

/**
 * Every option of the export modal, as saved on an export quote. Settings that
 * live outside the modal state (custom spend columns and cloud maturity in
 * localStorage, localize installs and sort on the page) are captured too, so
 * reopening a quote reproduces the export that was priced.
 */
export type ExportSettingsSnapshot = {
  version: 1;
  spendInclusion: EXPORT_INCLUSION_TYPES;
  aiSpendSheetInclusion: EXPORT_INCLUSION_TYPES;
  installInclusion: EXPORT_INCLUSION_TYPES;
  installProductIds: number[];
  installLookbackMonths: number | string;
  installIncludeTimeSeries: boolean;
  installFieldConfiguration: ExportFieldConfiguration;
  timeSeriesLookbackMonths: number | string;
  timeSeriesFieldConfiguration: ExportFieldConfiguration;
  faiInclusion: EXPORT_INCLUSION_TYPES;
  faiProductIds: number[];
  faiDepartmentIds: string[];
  faiRoleIds: string[];
  faiFieldConfiguration: ExportFieldConfiguration;
  includeDecisionMakersAndInfluencersOnly: boolean;
  contractsInclusion: EXPORT_INCLUSION_TYPES;
  contractFieldConfiguration: ExportFieldConfiguration;
  contractStatus: CONTRACT_STATUS_TYPES;
  intentInclusion: EXPORT_INCLUSION_TYPES;
  intentTopicIds: string[];
  intentTopicsCache: CachedTopic[];
  intentSignalScore: IntentSignalScoreUi;
  intentCollectionIds: string[];
  intentFieldConfiguration: ExportFieldConfiguration;
  buyerIntentInclusion: EXPORT_INCLUSION_TYPES;
  buyerIntentProductIds: number[];
  mentionInclusion: EXPORT_INCLUSION_TYPES;
  mentionIds: number[];
  mentionLookbackMonths: number | string;
  mentionIncludeTimeSeries: boolean;
  mentionFieldConfiguration: ExportFieldConfiguration;
  mentionTimeSeriesFieldConfiguration: ExportFieldConfiguration;
  generativeAIInclusion: EXPORT_INCLUSION_TYPES;
  generativeAIFieldConfiguration: ExportFieldConfiguration;
  spendFieldConfiguration: ExportFieldConfiguration;
  spendIncludeSMB: boolean;
  relativeSpendInclusion: EXPORT_INCLUSION_TYPES;
  relativeSpendIncludeSMB: boolean;
  relativeSpendFieldConfiguration: ExportFieldConfiguration;
  rowBasedSpendInclusion: EXPORT_INCLUSION_TYPES;
  rowBasedSpendIncludeSMB: boolean;
  rowBasedSpendFieldConfiguration: ExportFieldConfiguration;
  aiSpendInclusion: AI_SPEND_INCLUSION_TYPES;
  aiSpendIncludeSMB: boolean;
  aiSpendAbsoluteFieldConfiguration: ExportFieldConfiguration;
  aiSpendRelativeFieldConfiguration: ExportFieldConfiguration;
  contactsInclusion: EXPORT_INCLUSION_TYPES;
  contactsFieldConfiguration: ExportFieldConfiguration;
  contactsPerCompanyLimit: number | null;
  contactsFac: ContactsFacExportSettings;
  financingEventsInclusion: EXPORT_INCLUSION_TYPES;
  financingEventsFieldConfiguration: ExportFieldConfiguration;
  financingEventsFilters: FinancingEventsFilters;
  mergersAcquisitionsInclusion: EXPORT_INCLUSION_TYPES;
  mergersAcquisitionsFieldConfiguration: ExportFieldConfiguration;
  mergersAcquisitionsFilters: MergersAcquisitionsFilters;
  aiScoresInclusion: EXPORT_INCLUSION_TYPES;
  aiScoresSourceIds: string[];
  aiScoresTiers: string[];
  customSpendColumns: SelectableCustomColumn[];
  cloudMaturity: boolean;
  localizeInstalls: boolean;
  sort?: { field: string; direction: string };
};
